import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Copy, Check, ExternalLink, Globe, Mail, Shield,
  AlertCircle, CheckCircle2, Info, Server,
} from "lucide-react";

/**
 * Manual DNS-opsætnings guide til Simply.com.
 * Viser alle DNS-records med kopier-knapper og trin-for-trin instruktioner.
 */
export default function DnsSetup() {
  const [provider, setProvider] = useState("resend");
  const [copied, setCopied] = useState("");

  const copy = (val, id) => {
    navigator.clipboard.writeText(val);
    setCopied(id);
    setTimeout(() => setCopied(""), 1800);
  };

  // Emergent-hosted apps get their exact CNAME-target from Deploy → Custom Domain
  // panel AFTER first production deploy. This is a placeholder — user must copy
  // the actual value from Emergent's admin.
  const APP_HOST = "din-app.emergent.host";

  const providerConfig = {
    resend: {
      name: "Resend",
      spf: "v=spf1 include:_spf.resend.com ~all",
      dkim_host: "resend._domainkey",
      dkim_note: "DKIM-nøglen genereres af Resend, når du tilføjer domænet. Log ind på https://resend.com/domains, klik 'Add Domain', indtast findyournaseeb.com, og kopier de præcise værdier Resend viser dig.",
      dkim_url: "https://resend.com/domains",
      dmarc: "v=DMARC1; p=none; rua=mailto:hej@findyournaseeb.com",
    },
    sendgrid: {
      name: "SendGrid",
      spf: "v=spf1 include:sendgrid.net ~all",
      dkim_host: "s1._domainkey (og s2._domainkey)",
      dkim_note: "SendGrid genererer to CNAME-records (s1._domainkey og s2._domainkey). Log ind på https://app.sendgrid.com/settings/sender_auth, klik 'Authenticate Your Domain', indtast findyournaseeb.com, og kopier de præcise CNAME-værdier.",
      dkim_url: "https://app.sendgrid.com/settings/sender_auth",
      dmarc: "v=DMARC1; p=none; rua=mailto:hej@findyournaseeb.com",
    },
    gmail: {
      name: "Gmail (Google Workspace)",
      spf: "v=spf1 include:_spf.google.com ~all",
      dkim_host: "google._domainkey",
      dkim_note: "Log ind på https://admin.google.com → Apps → Google Workspace → Gmail → 'Authenticate email'. Generér en 2048-bit nøgle og kopier TXT-værdien.",
      dkim_url: "https://admin.google.com",
      dmarc: "v=DMARC1; p=none; rua=mailto:hej@findyournaseeb.com",
    },
  };

  const p = providerConfig[provider];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-7" data-testid="dns-setup-page">
      <Link to="/admin/settings" className="btn-ghost text-sm mb-4 inline-flex" data-testid="back-to-settings">
        <ArrowLeft className="h-4 w-4" /> Tilbage til indstillinger
      </Link>

      <div className="eyebrow">Domæne-opsætning</div>
      <h1 className="mt-2 font-serif text-4xl text-olive-700">DNS-guide: findyournaseeb.com</h1>
      <p className="mt-3 text-brown-600">
        Følg trinene nedenfor hos <strong>Simply.com</strong> for at pege domænet mod din Find Your Naseeb-app OG gøre email-afsendelse pålidelig (så mails ikke ryger i spam).
      </p>

      {/* Steps overview */}
      <div className="mt-8 grid gap-3 sm:grid-cols-3">
        <StepCard num="1" title="Peg domæne mod appen" icon={<Server className="h-4 w-4" />} desc="A-record til Emergent" />
        <StepCard num="2" title="Vælg email-udbyder" icon={<Mail className="h-4 w-4" />} desc="SPF + DKIM til Resend/SendGrid/Gmail" />
        <StepCard num="3" title="Tilføj DMARC" icon={<Shield className="h-4 w-4" />} desc="Beskyt mod spoofing" />
      </div>

      {/* Step 1: A record */}
      <Section num="1" title="Peg findyournaseeb.com mod appen" icon={<Globe className="h-5 w-5 text-olive-600" />}>
        <InfoBox tone="warning">
          <strong>Vigtigt:</strong> Den præcise CNAME-værdi får du fra Emergent's Deploy-panel EFTER du har deployet din app til produktion.
          Gå til <strong>Emergent → Deploy → Custom Domain</strong>, tilføj <code>findyournaseeb.com</code>, og kopier den værdi Emergent viser dig
          (eksempel: <code>xxxxxx.emergent.host</code>). Erstat placeholder-værdien nedenfor med den rigtige.
        </InfoBox>
        <p className="text-sm text-brown-600 mt-4 mb-4">
          Simply.com skal have en <strong>CNAME</strong> (eller ALIAS, hvis apex kræves) der peger dit domæne på Emergent's hosting.
        </p>
        <RecordCard
          type="CNAME"
          host="@"
          value={APP_HOST}
          ttl="3600"
          copyId="app-cname"
          copied={copied}
          onCopy={copy}
          note="'@' betyder root-domænet (findyournaseeb.com). Nogle registratorer kalder det ALIAS eller ANAME."
        />
        <RecordCard
          type="CNAME"
          host="www"
          value={APP_HOST}
          ttl="3600"
          copyId="www-cname"
          copied={copied}
          onCopy={copy}
          note="www.findyournaseeb.com skal også pege på appen."
        />
        <InfoBox tone="info">
          Efter du har gemt disse records, tager det typisk 15 min – 2 timer før ændringen er aktiv på hele internettet (DNS-propagation).
        </InfoBox>
      </Section>

      {/* Step 2: Email provider */}
      <Section num="2" title="Konfigurér din email-udbyder" icon={<Mail className="h-5 w-5 text-olive-600" />}>
        <p className="text-sm text-brown-600 mb-4">
          Vælg samme udbyder som du valgte i Admin → Indstillinger → Email:
        </p>
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(providerConfig).map(([k, v]) => (
            <button
              key={k}
              onClick={() => setProvider(k)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                provider === k
                  ? "bg-olive-500 text-white border-olive-500"
                  : "bg-white border-sand-300 text-brown-600 hover:border-olive-500"
              }`}
              data-testid={`dns-provider-${k}`}>
              {v.name}
            </button>
          ))}
        </div>

        {/* SPF */}
        <h3 className="font-serif text-lg text-olive-700 mt-6 mb-2">2a. SPF (Sender Policy Framework)</h3>
        <p className="text-sm text-brown-600 mb-3">
          SPF fortæller modtager-mailservere, at {p.name} har lov at sende mails fra findyournaseeb.com.
        </p>
        <RecordCard
          type="TXT"
          host="@"
          value={p.spf}
          ttl="3600"
          copyId="spf-record"
          copied={copied}
          onCopy={copy}
          note="Hvis du allerede har en SPF-record, tilføj ikke en ny — flet i stedet 'include:...' ind i den eksisterende record."
        />

        {/* DKIM */}
        <h3 className="font-serif text-lg text-olive-700 mt-8 mb-2">2b. DKIM (DomainKeys Identified Mail)</h3>
        <p className="text-sm text-brown-600 mb-3">
          DKIM signerer hver mail med en kryptografisk signatur. Du skal genereren nøglen hos {p.name} først:
        </p>
        <InfoBox tone="warning">
          {p.dkim_note}
        </InfoBox>
        <a
          href={p.dkim_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-sm text-olive-600 hover:text-olive-700 underline"
          data-testid={`open-${provider}-dashboard`}>
          <ExternalLink className="h-4 w-4" /> Åbn {p.name}-dashboard
        </a>
        <div className="mt-4 rounded-xl border border-sand-300 bg-sand-100 p-4 text-xs text-brown-600">
          <div className="font-medium text-brown-700 mb-1">Når du har fået DKIM-værdien fra {p.name}, tilføj den som:</div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <div><strong>Type:</strong> TXT</div>
            <div><strong>Host:</strong> {p.dkim_host}</div>
            <div><strong>Værdi:</strong> (indsæt fra {p.name})</div>
          </div>
        </div>

        {/* Return-Path */}
        {provider === "resend" && (
          <>
            <h3 className="font-serif text-lg text-olive-700 mt-8 mb-2">2c. Return-Path (Resend)</h3>
            <p className="text-sm text-brown-600 mb-3">
              Resend beder om en MX + TXT record for bounce-håndtering. Værdierne får du fra Resend når du tilføjer domænet — de er unikke pr. domæne.
            </p>
          </>
        )}
      </Section>

      {/* Step 3: DMARC */}
      <Section num="3" title="DMARC (spoofing-beskyttelse)" icon={<Shield className="h-5 w-5 text-olive-600" />}>
        <p className="text-sm text-brown-600 mb-3">
          DMARC fortæller modtager-mailservere hvad de skal gøre med mails som fejler SPF/DKIM. Starter med <code>p=none</code> for at overvåge, kan senere sættes til <code>quarantine</code> eller <code>reject</code>.
        </p>
        <RecordCard
          type="TXT"
          host="_dmarc"
          value={p.dmarc}
          ttl="3600"
          copyId="dmarc-record"
          copied={copied}
          onCopy={copy}
          note="Efter 2-4 uger uden fejl kan du hæve til p=quarantine, og senere p=reject for stærkere beskyttelse."
        />
      </Section>

      {/* Simply.com step-by-step */}
      <Section num="→" title="Sådan tilføjer du records i Simply.com" icon={<Info className="h-5 w-5 text-olive-600" />}>
        <ol className="space-y-3 text-sm text-brown-600 list-decimal list-inside">
          <li>Log ind på <a className="text-olive-600 underline" href="https://www.simply.com/dk/controlpanel/" target="_blank" rel="noopener noreferrer">Simply.com's kontrolpanel</a>.</li>
          <li>Klik på <strong>findyournaseeb.com</strong> i din domænelist.</li>
          <li>Vælg <strong>"DNS/Web-forwarding"</strong> i venstre menu.</li>
          <li>Klik <strong>"Redigér DNS-records"</strong> nederst.</li>
          <li>For hver record ovenfor:
            <ul className="mt-2 ml-6 space-y-1 list-disc text-xs text-brown-500">
              <li>Klik <strong>"Tilføj record"</strong></li>
              <li>Vælg <strong>Type</strong> (CNAME eller TXT)</li>
              <li>Indsæt <strong>Host/Subdomæne</strong> (fx <code>@</code> eller <code>_dmarc</code>)</li>
              <li>Indsæt <strong>Værdi/Data</strong> (den lange streng, kopieret fra dette panel)</li>
              <li>Sæt <strong>TTL</strong> til 3600 (1 time)</li>
              <li>Klik <strong>Gem</strong></li>
            </ul>
          </li>
          <li>Efter du har tilføjet alle records, vent 15 min – 2 timer og test din DNS med et gratis værktøj som <a className="text-olive-600 underline" href="https://mxtoolbox.com/spf.aspx" target="_blank" rel="noopener noreferrer">MXToolbox SPF Check</a>.</li>
          <li>Tilbage i Find Your Naseeb → Admin → Indstillinger → Email: klik <strong>"Send test-mail"</strong> for at bekræfte at det virker.</li>
        </ol>
      </Section>

      {/* Verification */}
      <Section num="✓" title="Verificér din opsætning" icon={<CheckCircle2 className="h-5 w-5 text-olive-600" />}>
        <p className="text-sm text-brown-600 mb-3">
          Brug disse gratis værktøjer til at verificere dine DNS-records efter propagation:
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          <VerifyLink label="Tjek A/CNAME" url="https://dnschecker.org/#CNAME/findyournaseeb.com" />
          <VerifyLink label="Tjek SPF" url="https://mxtoolbox.com/spf.aspx" />
          <VerifyLink label="Tjek DKIM" url="https://mxtoolbox.com/dkim.aspx" />
          <VerifyLink label="Tjek DMARC" url="https://mxtoolbox.com/dmarc.aspx" />
        </div>
      </Section>

      <div className="mt-10 text-center text-xs text-brown-400">
        Har du brug for hjælp? Kontakt {p.name}-support eller Simply.com-support med disse værdier.
      </div>
    </div>
  );
}

function StepCard({ num, title, icon, desc }) {
  return (
    <div className="card-soft p-4 flex items-start gap-3">
      <div className="h-8 w-8 rounded-full bg-olive-100 text-olive-700 flex items-center justify-center font-serif shrink-0">{num}</div>
      <div className="min-w-0">
        <div className="text-xs text-brown-500 flex items-center gap-1">{icon} Trin {num}</div>
        <div className="font-medium text-brown-700 mt-0.5">{title}</div>
        <div className="text-xs text-brown-500 mt-1">{desc}</div>
      </div>
    </div>
  );
}

function Section({ num, title, icon, children }) {
  return (
    <section className="mt-10 card-soft p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-full bg-olive-100 text-olive-700 flex items-center justify-center font-serif text-lg shrink-0">{num}</div>
        <h2 className="font-serif text-2xl text-olive-700 flex items-center gap-2">{icon} {title}</h2>
      </div>
      {children}
    </section>
  );
}

function RecordCard({ type, host, value, ttl, copyId, copied, onCopy, note }) {
  return (
    <div className="rounded-xl border border-sand-300 bg-white p-4 mb-3" data-testid={`dns-record-${copyId}`}>
      <div className="grid gap-3 sm:grid-cols-4 text-xs">
        <FieldReadOnly label="Type" value={type} testid={`${copyId}-type`} />
        <FieldReadOnly label="Host / Navn" value={host} testid={`${copyId}-host`} />
        <div className="sm:col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-brown-500 mb-1">Værdi / Data</div>
          <div className="flex items-start gap-2">
            <code className="flex-1 block bg-sand-100 rounded px-2 py-1.5 text-[11px] break-all font-mono" data-testid={`${copyId}-value`}>{value}</code>
            <button
              onClick={() => onCopy(value, copyId)}
              className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs whitespace-nowrap ${
                copied === copyId
                  ? "bg-olive-500 text-white border-olive-500"
                  : "bg-white border-olive-500 text-olive-600 hover:bg-olive-100"
              }`}
              data-testid={`${copyId}-copy-btn`}>
              {copied === copyId ? <><Check className="h-3.5 w-3.5" /> Kopieret</> : <><Copy className="h-3.5 w-3.5" /> Kopier</>}
            </button>
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] text-brown-500">
        <span><strong>TTL:</strong> {ttl}</span>
        {note && <span className="italic">💡 {note}</span>}
      </div>
    </div>
  );
}

function FieldReadOnly({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-brown-500 mb-1">{label}</div>
      <code className="block bg-sand-100 rounded px-2 py-1.5 text-[11px] font-mono" data-testid={testid}>{value}</code>
    </div>
  );
}

function InfoBox({ tone = "info", children }) {
  const toneStyle = tone === "warning"
    ? "bg-gold-100/60 border-gold-500/30 text-brown-700"
    : "bg-olive-50 border-olive-500/30 text-brown-700";
  return (
    <div className={`mt-3 rounded-xl border ${toneStyle} p-3 text-xs flex gap-2`}>
      {tone === "warning" ? <AlertCircle className="h-4 w-4 text-gold-700 shrink-0 mt-0.5" /> : <Info className="h-4 w-4 text-olive-600 shrink-0 mt-0.5" />}
      <div>{children}</div>
    </div>
  );
}

function VerifyLink({ label, url }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-between gap-2 rounded-xl border border-sand-300 bg-white hover:border-olive-500 hover:bg-olive-100/40 px-4 py-2.5 text-sm transition"
      data-testid={`verify-${label.toLowerCase().replace(/\W/g, "-")}`}>
      <span className="text-brown-700">{label}</span>
      <ExternalLink className="h-3.5 w-3.5 text-brown-500" />
    </a>
  );
}
